import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import {
    Loader2, RefreshCw, Pin, Ban, RotateCcw, Sparkles, Copy, Check,
    FileText, ChevronDown, ChevronRight, AlertTriangle,
} from "lucide-react";

// Iter 31.31 (Feb 2026) — No. 6 reported the CV page 504'ing on resume.
// Cause: fetching `limit=1000` selections in one shot; the inline-summary
// join over 1000 ids broke the CDN's 60s ceiling. Switched to paged loads
// (CV_PAGE_SIZE per request) appended into a scrollable window with an
// IntersectionObserver sentinel that pulls the next page when the operator
// scrolls toward the bottom. Counts still come from the server-side
// aggregation so the stats strip stays accurate independent of page size.
const CV_PAGE_SIZE = 100;

// CV / Résumé page. The system selects, the system composes; the user only
// pins or vetoes. Output is standard recruiter prose (No. 6's call, 9 May 2026).

const CATEGORY_LABELS = {
    certificate: "Certifications",
    achievement: "Achievements",
    publication: "Publications & Deliverables",
    delivery: "Deliveries",
    milestone: "Milestones",
};

function StampHeader() {
    return (
        <div className="mb-6">
            <div className="label-typed tw-prefix">Drawn from the Shed · the system selects · the system composes</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-[0.04em] text-ink mt-1">
                Résumé<span className="cursor-blink" />
            </h1>
            <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                Every shred is scored for résumé-worthiness. Certifications and milestones land
                automatically; private domains stay out by default. You may pin or veto at the
                entry level — composition is not yours to edit. Output is standard recruiter prose.
            </p>
        </div>
    );
}

function CopyButton({ text, testid }) {
    const [done, setDone] = useState(false);
    const onClick = async () => {
        if (!text) return;
        try { await navigator.clipboard.writeText(text); } catch { /* no-op */ }
        setDone(true);
        setTimeout(() => setDone(false), 1500);
    };
    return (
        <button
            onClick={onClick}
            disabled={!text}
            data-testid={testid}
            className="inline-flex items-center gap-2 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-3 py-1.5 font-heading uppercase text-[10px] tracking-[0.25em] disabled:opacity-40"
        >
            {done ? <Check className="w-3 h-3 text-stamp-green" /> : <Copy className="w-3 h-3" />}
            {done ? "Copied" : "Copy"}
        </button>
    );
}

function DecisionPill({ decision }) {
    const map = {
        "auto-include": "border-stamp-green text-stamp-green",
        "user-pinned": "bg-stamp-red text-paper-light",
        "auto-exclude": "border-ink/30 text-ink-soft",
        "user-vetoed": "border-ink/40 text-ink line-through",
    };
    const label = {
        "auto-include": "Auto-included",
        "user-pinned": "Pinned",
        "auto-exclude": "Auto-excluded",
        "user-vetoed": "Vetoed",
    }[decision] || decision;
    return (
        <span className={`inline-block border px-2 py-0.5 font-heading uppercase text-[9px] tracking-[0.18em] ${map[decision] || "border-ink/30 text-ink"}`}>
            {label}
        </span>
    );
}

function SelectionRow({ s, onPin, onVeto, onClear, busy }) {
    const e = s.entry || {};
    const ts = (s.entry_timestamp || "").slice(0, 10);
    const isAuto = s.decision.startsWith("auto-");
    return (
        <li className="py-2 border-b border-ink/10" data-testid={`cv-row-${s.entry_id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <DecisionPill decision={s.decision} />
                        <span className="label-typed text-[9px]">{ts}</span>
                        <span className="label-typed text-[9px]">{e.domain || s.entry_domain}</span>
                        <span className="label-typed text-[9px]">w {s.weight.toFixed(2)}</span>
                    </div>
                    <div className="font-body text-sm text-ink leading-snug">
                        {s.cv_phrase || e.summary || "(no summary)"}
                    </div>
                    <div className="label-typed text-[9px] mt-1 italic">{s.rationale}</div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                    {!["user-pinned"].includes(s.decision) && (
                        <button
                            onClick={() => onPin(s.entry_id)}
                            disabled={busy}
                            data-testid={`cv-pin-${s.entry_id}`}
                            className="inline-flex items-center gap-1 border border-stamp-red/40 text-stamp-red hover:bg-stamp-red hover:text-paper-light px-2 py-1 font-heading uppercase text-[9px] tracking-[0.22em]"
                            title="Force include in résumé"
                        >
                            <Pin className="w-3 h-3" /> Pin
                        </button>
                    )}
                    {!["user-vetoed"].includes(s.decision) && (
                        <button
                            onClick={() => onVeto(s.entry_id)}
                            disabled={busy}
                            data-testid={`cv-veto-${s.entry_id}`}
                            className="inline-flex items-center gap-1 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-2 py-1 font-heading uppercase text-[9px] tracking-[0.22em]"
                            title="Force exclude from résumé"
                        >
                            <Ban className="w-3 h-3" /> Veto
                        </button>
                    )}
                    {!isAuto && (
                        <button
                            onClick={() => onClear(s.entry_id)}
                            disabled={busy}
                            data-testid={`cv-clear-${s.entry_id}`}
                            className="inline-flex items-center gap-1 border border-ink/30 hover:border-ink hover:text-ink px-2 py-1 font-heading uppercase text-[9px] tracking-[0.22em] text-ink-soft"
                            title="Revert to auto"
                        >
                            <RotateCcw className="w-3 h-3" /> Auto
                        </button>
                    )}
                </div>
            </div>
        </li>
    );
}

function CategoryGroup({ category, items, onPin, onVeto, onClear, busy }) {
    if (!items.length) return null;
    return (
        <div className="mb-5" data-testid={`cv-category-${category}`}>
            <div className="flex items-baseline gap-2 mb-2">
                <h3 className="font-heading uppercase tracking-[0.18em] text-sm text-ink">
                    {CATEGORY_LABELS[category] || category}
                </h3>
                <span className="label-typed">{items.length}</span>
            </div>
            <ul>
                {items.map((s) => (
                    <SelectionRow key={s.entry_id} s={s} onPin={onPin} onVeto={onVeto} onClear={onClear} busy={busy} />
                ))}
            </ul>
        </div>
    );
}

function IdentityForm({ identity, setIdentity }) {
    return (
        <div className="paper-card border-2 border-dashed border-ink/30 p-4" data-testid="cv-identity-form">
            <div className="label-typed mb-3">Identity · header of the composed résumé</div>
            <div className="grid md:grid-cols-2 gap-2">
                <input
                    value={identity.display_name}
                    onChange={(e) => setIdentity({ ...identity, display_name: e.target.value })}
                    placeholder="Display name (e.g. Matt Surname)"
                    data-testid="cv-id-name"
                    className="bg-paper-light/60 border border-ink/20 px-3 py-2 font-body text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-stamp-red"
                />
                <input
                    value={identity.target_role}
                    onChange={(e) => setIdentity({ ...identity, target_role: e.target.value })}
                    placeholder="Target role (e.g. Operations Lead)"
                    data-testid="cv-id-role"
                    className="bg-paper-light/60 border border-ink/20 px-3 py-2 font-body text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-stamp-red"
                />
                <input
                    value={identity.contact_line}
                    onChange={(e) => setIdentity({ ...identity, contact_line: e.target.value })}
                    placeholder="Contact line (city · email · website — your call)"
                    data-testid="cv-id-contact"
                    className="md:col-span-2 bg-paper-light/60 border border-ink/20 px-3 py-2 font-body text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-stamp-red"
                />
                <textarea
                    value={identity.summary_hint}
                    onChange={(e) => setIdentity({ ...identity, summary_hint: e.target.value })}
                    placeholder="Optional summary hint — one line steer for the personal statement (e.g. 'AI-augmented operations and content design')"
                    data-testid="cv-id-hint"
                    rows={2}
                    className="md:col-span-2 bg-paper-light/60 border border-ink/20 px-3 py-2 font-body text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-stamp-red"
                />
            </div>
            <div className="label-typed text-[9px] italic mt-2">
                Stored locally in the browser. The Shed does not file this.
            </div>
        </div>
    );
}

export default function Cv() {
    const [data, setData] = useState(null);
    const [docs, setDocs] = useState([]);
    const [openDoc, setOpenDoc] = useState(null);
    const [busy, setBusy] = useState(false);
    const [composing, setComposing] = useState(false);
    const [err, setErr] = useState("");
    const [showExcluded, setShowExcluded] = useState(false);
    const [identity, setIdentity] = useState(() => {
        try {
            const raw = localStorage.getItem("shed_cv_identity");
            return raw ? JSON.parse(raw) : { display_name: "", target_role: "", contact_line: "", summary_hint: "" };
        } catch {
            return { display_name: "", target_role: "", contact_line: "", summary_hint: "" };
        }
    });
    const [format, setFormat] = useState(() => localStorage.getItem("shed_cv_format") || "traditional");
    const [targetJd, setTargetJd] = useState(() => localStorage.getItem("shed_cv_target_jd") || "");

    useEffect(() => {
        localStorage.setItem("shed_cv_identity", JSON.stringify(identity));
    }, [identity]);
    useEffect(() => { localStorage.setItem("shed_cv_format", format); }, [format]);
    useEffect(() => { localStorage.setItem("shed_cv_target_jd", targetJd); }, [targetJd]);

    const load = async () => {
        try {
            const [sels, dlist] = await Promise.all([
                api.get("/cv/selections", { params: { limit: CV_PAGE_SIZE, offset: 0 } }),
                api.get("/cv/documents"),
            ]);
            // First page replaces the buffer; subsequent pages append.
            setData({ ...sels.data, items: sels.data.items || [] });
            setDocs(dlist.data.items || []);
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "no-response"}] ${e.response?.data?.detail || e.message}`);
        }
    };

    // Pagination state for the selections list. The server returns
    // has_more + offset/limit so we can pull the next slice on scroll.
    const [pageBusy, setPageBusy] = useState(false);
    const loadMore = useCallback(async () => {
        if (!data || !data.has_more || pageBusy) return;
        setPageBusy(true);
        try {
            const nextOffset = (data.offset || 0) + (data.limit || CV_PAGE_SIZE);
            const { data: page } = await api.get("/cv/selections", {
                params: { limit: CV_PAGE_SIZE, offset: nextOffset, include_counts: false },
            });
            setData((prev) => ({
                ...prev,
                // The paged response carries empty counts; keep the originals
                // from the first page so the stats strip stays accurate.
                items: [...(prev?.items || []), ...(page.items || [])],
                offset: page.offset,
                limit: page.limit,
                has_more: page.has_more,
            }));
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally {
            setPageBusy(false);
        }
    }, [data, pageBusy]);

    useEffect(() => { load(); }, []);

    const score = async () => {
        setBusy(true); setErr("");
        try {
            await api.post("/cv/score", {});
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    const action = async (verb, entryId) => {
        setBusy(true); setErr("");
        try {
            await api.post(`/cv/selections/${entryId}/${verb}`, {});
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    const compose = async () => {
        setComposing(true); setErr("");
        try {
            const { data: doc } = await api.post("/cv/compose", {
                identity,
                format,
                target_jd: targetJd?.trim() ? targetJd.trim() : undefined,
            });
            setOpenDoc(doc);
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status}] ${e.response?.data?.detail || e.message}`);
        } finally { setComposing(false); }
    };

    // ---------- Cost dial (iter 26.3) ----------
    // Dry-run estimate auto-refreshes when selections change OR when the
    // operator tweaks format / target_jd / identity. Debounced so the user
    // typing a JD doesn't hammer the endpoint.
    const [estimate, setEstimate] = useState(null);
    const [estimateBusy, setEstimateBusy] = useState(false);
    const [estimateErr, setEstimateErr] = useState("");
    // AbortController for the dry-run poll. When the user types a JD
    // fast, multiple fetches can be in-flight; the last response would
    // otherwise win out-of-order and overwrite a stale estimate over a
    // fresh one. Abort the prior request before firing the next, and
    // also on unmount (iter 26.10, 14 May 2026).
    const estimateAbortRef = useRef(null);

    const fetchEstimate = async () => {
        if (!data || !(data.by_decision?.["auto-include"] || data.by_decision?.["user-pinned"])) {
            setEstimate(null); setEstimateErr(""); return;
        }
        // Cancel any in-flight estimate from a previous keystroke
        if (estimateAbortRef.current) {
            estimateAbortRef.current.abort();
        }
        const controller = new AbortController();
        estimateAbortRef.current = controller;
        setEstimateBusy(true); setEstimateErr("");
        try {
            const { data: est } = await api.post("/cv/compose", {
                identity, format,
                target_jd: targetJd?.trim() ? targetJd.trim() : undefined,
                dry_run: true,
            }, { signal: controller.signal });
            // Late-arriving response from a now-cancelled request? Drop it.
            if (controller.signal.aborted) return;
            setEstimate(est);
        } catch (e) {
            // Aborts surface as either CanceledError (axios) or AbortError
            // (DOMException). Either way: silent, this isn't a real failure.
            const isAbort = e?.name === "CanceledError"
                || e?.name === "AbortError"
                || e?.code === "ERR_CANCELED"
                || controller.signal.aborted;
            if (isAbort) return;
            setEstimateErr(e.response?.data?.detail || e.message || "estimate failed");
            setEstimate(null);
        } finally {
            // Only clear busy if THIS request is still the current one
            if (estimateAbortRef.current === controller) {
                setEstimateBusy(false);
                estimateAbortRef.current = null;
            }
        }
    };
    useEffect(() => {
        const t = setTimeout(fetchEstimate, 500);
        return () => {
            clearTimeout(t);
            // Unmount or deps changed mid-debounce — also kill any in-flight
            if (estimateAbortRef.current) {
                estimateAbortRef.current.abort();
                estimateAbortRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, format, targetJd]);
    // -------------------------------------------

    const openExisting = async (id) => {
        try {
            const { data: doc } = await api.get(`/cv/documents/${id}`);
            setOpenDoc(doc);
        } catch (e) {
            setErr(`[HTTP ${e.response?.status}] ${e.response?.data?.detail || e.message}`);
        }
    };

    const grouped = useMemo(() => {
        if (!data) return { included: {}, excluded: [] };
        const inc = {};
        const exc = [];
        for (const s of data.items) {
            if (s.decision === "auto-include" || s.decision === "user-pinned") {
                inc[s.category] = inc[s.category] || [];
                inc[s.category].push(s);
            } else {
                exc.push(s);
            }
        }
        return { included: inc, excluded: exc };
    }, [data]);

    return (
        <Shell>
            <StampHeader />

            {err && (
                <div className="paper-card border-2 border-stamp-red p-4 mb-4" data-testid="cv-error">
                    <div className="flex items-center justify-between">
                        <span className="stamp-red font-heading text-sm tracking-[0.2em]">FAILURE ON FILE</span>
                        <button onClick={() => setErr("")} className="font-heading uppercase text-[10px] tracking-[0.2em] text-ink-soft hover:text-stamp-red">clear</button>
                    </div>
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words mt-2">{err}</pre>
                </div>
            )}

            {/* Stats + actions */}
            {data && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="cv-stats">
                    <div className="paper-card p-3">
                        <div className="label-typed">On file</div>
                        <div className="font-heading text-2xl text-ink">{data.total ?? data.count}</div>
                    </div>
                    <div className="paper-card p-3">
                        <div className="label-typed">Auto-include</div>
                        <div className="font-heading text-2xl text-stamp-green">{data.by_decision["auto-include"] || 0}</div>
                    </div>
                    <div className="paper-card p-3">
                        <div className="label-typed">Pinned</div>
                        <div className="font-heading text-2xl text-stamp-red">{data.by_decision["user-pinned"] || 0}</div>
                    </div>
                    <div className="paper-card p-3">
                        <div className="label-typed">Auto-exclude</div>
                        <div className="font-heading text-2xl text-ink-soft">{data.by_decision["auto-exclude"] || 0}</div>
                    </div>
                    <div className="paper-card p-3">
                        <div className="label-typed">Vetoed</div>
                        <div className="font-heading text-2xl text-ink-soft">{data.by_decision["user-vetoed"] || 0}</div>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap gap-2 mb-6 items-stretch">
                <button
                    onClick={score}
                    disabled={busy}
                    data-testid="cv-rescore-btn"
                    className="inline-flex items-center gap-2 border border-ink/40 hover:border-stamp-red hover:text-stamp-red px-4 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Re-score Ledger
                </button>
                <button
                    onClick={compose}
                    disabled={composing || !data}
                    data-testid="cv-compose-btn"
                    className="inline-flex items-center gap-2 bg-ink text-paper-light hover:bg-stamp-red px-4 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                >
                    {composing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {composing ? "Composing… typically 30–60s" : "Compose résumé"}
                    {estimate && !composing && (
                        <span className="text-[10px] tracking-[0.15em] opacity-80">
                            · ~${estimate.estimated_usd.toFixed(3)}
                        </span>
                    )}
                </button>
                <CostDial estimate={estimate} busy={estimateBusy} err={estimateErr} />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div>
                    <div className="paper-card tape-corner p-5 mb-4" data-testid="cv-included-block">
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-heading uppercase tracking-[0.22em] text-sm text-ink">
                                Selected for the résumé
                            </div>
                            {data && (
                                <span className="label-typed text-[9px]">
                                    {(data.items || []).length} loaded · {data.total ?? 0} total
                                </span>
                            )}
                        </div>
                        {Object.keys(grouped.included).length === 0 ? (
                            <div className="font-body text-sm text-ink-faint italic">
                                Nothing selected yet. Click <em>Re-score Ledger</em> to scan, or pin entries below.
                            </div>
                        ) : (
                            <div
                                className="max-h-[640px] overflow-auto pr-1"
                                data-testid="cv-included-scroll"
                            >
                                {Object.entries(grouped.included).map(([cat, items]) => (
                                    <CategoryGroup
                                        key={cat}
                                        category={cat}
                                        items={items}
                                        onPin={(id) => action("pin", id)}
                                        onVeto={(id) => action("veto", id)}
                                        onClear={(id) => action("clear", id)}
                                        busy={busy}
                                    />
                                ))}
                                {data?.has_more && (
                                    <LoadMoreSentinel
                                        onVisible={loadMore}
                                        busy={pageBusy}
                                        testid="cv-included-sentinel"
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    <div className="paper-card p-5">
                        <button
                            onClick={() => setShowExcluded(!showExcluded)}
                            className="w-full flex items-center justify-between font-heading uppercase tracking-[0.22em] text-sm text-ink-soft hover:text-stamp-red"
                            data-testid="cv-excluded-toggle"
                        >
                            <span className="flex items-center gap-2">
                                {showExcluded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                Excluded ({grouped.excluded.length}{data?.has_more ? "+" : ""})
                            </span>
                            <span className="label-typed text-[9px]">click to reveal · pin one back in</span>
                        </button>
                        {showExcluded && (
                            <div className="mt-3" data-testid="cv-excluded-list">
                                {grouped.excluded.length === 0 ? (
                                    <div className="font-body text-sm text-ink-faint italic">Nothing excluded.</div>
                                ) : (
                                    <div className="max-h-[480px] overflow-auto pr-1" data-testid="cv-excluded-scroll">
                                        <ul>
                                            {grouped.excluded.map((s) => (
                                                <SelectionRow
                                                    key={s.entry_id} s={s}
                                                    onPin={(id) => action("pin", id)}
                                                    onVeto={(id) => action("veto", id)}
                                                    onClear={(id) => action("clear", id)}
                                                    busy={busy}
                                                />
                                            ))}
                                        </ul>
                                        {data?.has_more && (
                                            <LoadMoreSentinel
                                                onVisible={loadMore}
                                                busy={pageBusy}
                                                testid="cv-excluded-sentinel"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <IdentityForm identity={identity} setIdentity={setIdentity} />

                    <div className="paper-card border-2 border-dashed border-ink/30 p-4 mt-4" data-testid="cv-format-form">
                        <div className="label-typed mb-3">Format · steer for the composer</div>
                        <div className="grid md:grid-cols-2 gap-2 mb-3">
                            {[
                                { v: "traditional", t: "Traditional", h: "ATS-friendly · sections in classic order" },
                                { v: "modern", t: "Modern", h: "Headline + Highlights · metrics-led bullets" },
                                { v: "skills-first", t: "Skills-first", h: "Competencies + Evidence · breadth over time" },
                                { v: "narrative", t: "Narrative", h: "Portfolio profile · for human readers" },
                            ].map((opt) => (
                                <button
                                    key={opt.v}
                                    onClick={() => setFormat(opt.v)}
                                    data-testid={`cv-format-${opt.v}`}
                                    className={`text-left border px-3 py-2 ${
                                        format === opt.v
                                            ? "border-stamp-red bg-stamp-red/5"
                                            : "border-ink/20 hover:border-ink/50"
                                    }`}
                                >
                                    <div className={`font-heading uppercase text-[11px] tracking-[0.22em] ${format === opt.v ? "text-stamp-red" : "text-ink"}`}>
                                        {opt.t}
                                    </div>
                                    <div className="label-typed text-[9px] mt-0.5">{opt.h}</div>
                                </button>
                            ))}
                        </div>
                        <textarea
                            value={targetJd}
                            onChange={(e) => setTargetJd(e.target.value)}
                            placeholder="Optional · paste a target job description. The composer will tilt section ordering and emphasis toward it. Will not invent matches that the Ledger does not support."
                            data-testid="cv-target-jd"
                            rows={5}
                            className="w-full bg-paper-light/60 border border-ink/20 px-3 py-2 font-body text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-stamp-red"
                        />
                        <div className="label-typed text-[9px] italic mt-2 flex items-center justify-between">
                            <span>{targetJd.trim() ? `${targetJd.trim().length} chars · steer engaged` : "no target JD · generalist composition"}</span>
                            {targetJd && (
                                <button
                                    onClick={() => setTargetJd("")}
                                    data-testid="cv-target-jd-clear"
                                    className="font-heading uppercase text-[9px] tracking-[0.22em] text-ink-soft hover:text-stamp-red"
                                >clear</button>
                            )}
                        </div>
                    </div>

                    <div className="paper-card p-5 mt-4">
                        <div className="font-heading uppercase tracking-[0.22em] text-sm text-ink mb-3">
                            Composed résumés
                        </div>
                        {docs.length === 0 ? (
                            <div className="font-body text-sm text-ink-faint italic">
                                No résumés composed yet. Click <em>Compose résumé</em>.
                            </div>
                        ) : (
                            <ul className="divide-y divide-ink/10">
                                {docs.map((d) => (
                                    <li
                                        key={d.id}
                                        className="py-2 cursor-pointer hover:bg-paper-light/50 px-2"
                                        onClick={() => openExisting(d.id)}
                                        data-testid={`cv-doc-row-${d.id}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-heading uppercase text-xs tracking-[0.06em] text-ink">
                                                <FileText className="w-3 h-3 inline mr-1" />
                                                {d.identity?.display_name || "Operator"}
                                                {d.identity?.target_role ? ` · ${d.identity.target_role}` : ""}
                                            </span>
                                            <span className="label-typed text-[9px]">{d.entries_count} entries</span>
                                        </div>
                                        <div className="label-typed text-[9px] mt-1">
                                            {d.generated_at?.slice(0, 19).replace("T", " ")} · {d.voice} · {d.format || "traditional"}
                                            {d.target_jd_used ? " · JD-steered" : ""}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {openDoc && (
                        <div className="paper-card p-5 mt-4 border-2 border-stamp-red" data-testid="cv-doc-preview">
                            <div className="flex items-center justify-between mb-3">
                                <div className="font-heading uppercase tracking-[0.22em] text-sm text-stamp-red">
                                    Composed · {openDoc.identity?.display_name || "Operator"}
                                </div>
                                <CopyButton text={openDoc.body_md} testid="cv-doc-copy" />
                            </div>
                            <pre className="font-body text-sm text-ink whitespace-pre-wrap leading-relaxed bg-paper-light/40 border border-ink/15 p-4 max-h-[600px] overflow-auto">
                                {openDoc.body_md}
                            </pre>
                            <div className="label-typed text-[9px] italic mt-2">
                                {openDoc.format || "traditional"} · {openDoc.voice} · v0 · {openDoc.entries_count} entries fed the model
                                {openDoc.target_jd_used ? " · JD-steered" : ""}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-10 hairline" />
            <div className="mt-4 label-typed text-center flex items-center justify-center gap-2">
                <AlertTriangle className="w-3 h-3" />
                The system selects · the system composes · originals untouched (Rule 1)
            </div>
        </Shell>
    );
}



/**
 * LoadMoreSentinel — placed at the bottom of a scrollable container.
 * When it scrolls into view, fires `onVisible()` once per visibility
 * transition. Used to trigger infinite-load paging on the CV selections
 * window (iter 31.31). `root: null` would observe the viewport; we pass
 * the closest scrollable ancestor by walking up parentElement so the
 * sentinel works inside the contained `max-h-[640px] overflow-auto` box.
 */
function LoadMoreSentinel({ onVisible, busy, testid }) {
    const ref = useRef(null);
    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        // Find the nearest scrollable ancestor so the IO root matches it.
        let root = node.parentElement;
        while (root && root !== document.body) {
            const style = window.getComputedStyle(root);
            if (/(auto|scroll)/.test(style.overflowY)) break;
            root = root.parentElement;
        }
        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) onVisible?.();
                }
            },
            { root: root === document.body ? null : root, rootMargin: "120px 0px" },
        );
        io.observe(node);
        return () => io.disconnect();
    }, [onVisible]);
    return (
        <div
            ref={ref}
            className="py-4 flex items-center justify-center label-typed text-[10px] tracking-[0.22em] text-ink-soft"
            data-testid={testid}
        >
            {busy ? (
                <>
                    <Loader2 className="w-3 h-3 animate-spin mr-2" /> Loading more…
                </>
            ) : (
                <span>Scroll to load more</span>
            )}
        </div>
    );
}


/**
 * CostDial — small estimate strip shown next to the Compose button.
 * Colour-graded: green ≤ 50K input tokens, amber 50K–200K, red > 200K.
 * Lives off the dry-run /cv/compose response (no LLM call, ~10ms).
 */
function CostDial({ estimate, busy, err }) {
    if (err) {
        return (
            <div
                className="paper-card border border-stamp-red/60 px-3 py-2 flex items-center gap-2 text-stamp-red label-typed text-[9px]"
                data-testid="cv-cost-dial-error"
            >
                Estimate · {err}
            </div>
        );
    }
    if (!estimate) {
        return (
            <div
                className="paper-card border border-dashed border-ink/30 px-3 py-2 flex items-center gap-2 label-typed text-[9px] text-ink-faint"
                data-testid="cv-cost-dial-empty"
            >
                {busy ? "Estimating…" : "Cost · waiting on selections"}
            </div>
        );
    }
    const tokIn = estimate.estimated_input_tokens;
    const band  = tokIn > 200_000 ? "red" : tokIn > 50_000 ? "amber" : "green";
    const bandClass = {
        green: "text-stamp-green border-stamp-green/60",
        amber: "text-amber-400 border-amber-400/60",
        red:   "text-stamp-red border-stamp-red/60",
    }[band];
    const bandLabel = { green: "cheap", amber: "modest", red: "watch this" }[band];
    return (
        <div
            className={`paper-card border ${bandClass} px-4 py-2 flex items-center gap-4 label-typed text-[10px]`}
            data-testid="cv-cost-dial"
        >
            <div className="flex flex-col">
                <span className="font-heading uppercase tracking-[0.2em] opacity-70">Estimated cost</span>
                <span className="font-heading text-base tracking-[0.04em]">
                    ${estimate.estimated_usd.toFixed(3)} <span className="opacity-70 text-[10px]">USD</span>
                </span>
            </div>
            <div className="flex flex-col">
                <span className="opacity-70">tokens in / out</span>
                <span>
                    {tokIn.toLocaleString()} / ≤{estimate.estimated_output_tokens_ceiling.toLocaleString()}
                </span>
            </div>
            <div className="flex flex-col">
                <span className="opacity-70">prompt size</span>
                <span>{(estimate.prompt_chars / 1024).toFixed(1)} KB</span>
            </div>
            <div className="flex flex-col">
                <span className="opacity-70">entries</span>
                <span>{estimate.selected_entries.toLocaleString()}</span>
            </div>
            <span
                className={`px-2 py-0.5 border ${bandClass} font-heading uppercase text-[9px] tracking-[0.2em] self-center`}
                data-testid="cv-cost-dial-band"
            >
                {bandLabel}
            </span>
            {busy && <Loader2 className="w-3 h-3 animate-spin opacity-60" />}
        </div>
    );
}
